import { type ReactNode } from "react";

// Bundled brand marks. Imported with `?url` so Vite emits a content-hashed, SAME-ORIGIN asset that
// loads under the BFF's strict `img-src 'self' data:` CSP (no remote favicons — those are blocked).
import githubUrl from "../../../assets/app-icons/github.svg?url";
import slackUrl from "../../../assets/app-icons/slack.svg?url";
import postgresUrl from "../../../assets/app-icons/postgres.svg?url";
import gitlabUrl from "../../../assets/app-icons/gitlab.svg?url";
import jiraUrl from "../../../assets/app-icons/jira.svg?url";
import notionUrl from "../../../assets/app-icons/notion.svg?url";
import openaiUrl from "../../../assets/app-icons/openai.svg?url";
import awsUrl from "../../../assets/app-icons/aws.svg?url";
import googleUrl from "../../../assets/app-icons/google.svg?url";
import stripeUrl from "../../../assets/app-icons/stripe.svg?url";

/**
 * App / tool brand icon. Maps a free-text app name (e.g. "GithubMCP", "Slack bot", "PostgreSQL") to
 * a bundled brand mark by matching known keywords; anything unrecognised falls back to a colored
 * lettered tile (first initial), so every app still gets a stable, legible glyph.
 *
 * Real favicons from app domains are intentionally NOT used: the operator BFF serves a strict
 * `img-src 'self' data:` CSP, which blocks remote images, and the app model carries no domain to
 * derive one from. Bundling marks keeps icons same-origin, crisp, and available offline.
 */

interface BrandEntry {
  readonly url: string;
  /** Lowercase keywords that identify this brand within an app name. */
  readonly match: readonly string[];
}

const BRANDS: readonly BrandEntry[] = [
  { url: githubUrl, match: ["github"] },
  { url: gitlabUrl, match: ["gitlab"] },
  { url: slackUrl, match: ["slack"] },
  { url: postgresUrl, match: ["postgres", "postgresql", "psql"] },
  { url: jiraUrl, match: ["jira", "atlassian"] },
  { url: notionUrl, match: ["notion"] },
  { url: openaiUrl, match: ["openai", "gpt", "chatgpt"] },
  { url: awsUrl, match: ["aws", "amazon", "s3", "lambda", "dynamodb"] },
  { url: googleUrl, match: ["google", "gcp", "gmail", "gdrive"] },
  { url: stripeUrl, match: ["stripe"] },
];

/** The brand asset URL for an app name, or null when nothing matches. */
export function brandIconFor(name: string): string | null {
  const n = name.toLowerCase();
  for (const b of BRANDS) {
    if (b.match.some((k) => n.includes(k))) return b.url;
  }
  return null;
}

/** A stable palette for the lettered fallback tile, chosen deterministically from the name. */
const TILE_COLORS = ["#1e4562", "#5f8aa5", "#7aa7c4", "#f0ab3d", "#e86a5c", "#8f6bd4", "#2bb5a0"];

function tileColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return TILE_COLORS[Math.abs(hash) % TILE_COLORS.length] ?? TILE_COLORS[0]!;
}

export function AppIcon(props: { name: string; size?: number }): ReactNode {
  const { name, size = 20 } = props;
  const url = brandIconFor(name);
  const dim = `${size}px`;

  if (url !== null) {
    return (
      <img
        className="adna-app-icon"
        src={url}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        style={{ width: dim, height: dim }}
      />
    );
  }

  // Fallback: a colored tile with the first letter of the app name.
  const letter = (name.trim()[0] ?? "?").toUpperCase();
  return (
    <span
      className="adna-app-icon adna-app-icon--tile"
      aria-hidden="true"
      style={{ width: dim, height: dim, background: tileColor(name) }}
    >
      {letter}
    </span>
  );
}
